import { UserInfoUserRole, UserInfoUserType } from "../api/interfaces/user-profile.interface";
import { Permission } from "../api/interfaces/permissions.interface";
import { Context } from "@/util-components/root/context";
import { useContext, useMemo } from "react";

/**
 * Interface for the permissions state object.
 */
export interface PermissionsState {
  /** Array of raw permission strings assigned to the user. */
  permissions: string[];
  /** Checks if the user has a specific permission. */
  hasPermission: (permission: Permission | string) => boolean;
  /** Checks if the user has at least one of the provided permissions. */
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean;
  /** Checks if the user has all of the provided permissions. */
  hasAllPermissions: (permissions: (Permission | string)[]) => boolean;
  
  // Aliases and Helpers
  /** Alias for hasPermission. Checks if the user has a specific permission. */
  can: (permission: Permission | string) => boolean;
  /** True if the user is an internal staff member (not a customer). */
  isInternal: boolean;
  /** True if the user is a customer. */
  isCustomer: boolean;
  /** True if the user has the ADMIN role. */
  isAdmin: boolean;
}

/**
 * Hook to check user permissions and roles.
 * 
 * Provides utility functions to verify if the current user has specific permissions
 * or belongs to certain user types/roles.
 * 
 * @returns {PermissionsState} An object containing permission checkers and role flags.
 * 
 * @example
 * const { can, isInternal, hasAnyPermission } = usePermissions();
 * 
 * if (isInternal) {
 *   return <InternalDashboard />;
 * }
 * 
 * if (can(Permission.MANAGE_CUSTOMERS)) {
 *   return <CustomerManagement />;
 * }
 */
export const usePermissions = (): PermissionsState => {
  const { user } = useContext(Context);

  const permissions = useMemo(() => user?.permissions || [], [user]);

  const hasPermission = useMemo(
    () => (permission: Permission | string) => permissions.includes(permission),
    [permissions]
  );

  const hasAnyPermission = useMemo(
    () => (requiredPermissions: (Permission | string)[]) => requiredPermissions.some((p) => permissions.includes(p)),
    [permissions]
  );

  const hasAllPermissions = useMemo(
    () => (requiredPermissions: (Permission | string)[]) => requiredPermissions.every((p) => permissions.includes(p)),
    [permissions]
  );

  const isInternal = useMemo(() => user?.userType === UserInfoUserType.INTERNAL_USER, [user]);
  const isCustomer = useMemo(() => user?.userType === UserInfoUserType.CUSTOMER_USER, [user]);
  const isAdmin = useMemo(() => user?.userRole === UserInfoUserRole.ADMIN, [user]);

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    can: hasPermission,
    isInternal,
    isCustomer,
    isAdmin,
  };
};
